import axios from "axios";
import AuthTeams from "./AuthTeams";
import CustomerConfig from "../azure/CustomerConfig";
import { authentication } from "@microsoft/teams-js";
import {
  TEAMS_TOKEN_FAILURE,
  AZURE_USER_AUTHENTICATION_FAILURE,
  SERVER_RESPONSE_ERROR,
  SERVER_RESPONSE_ISSUE,
  AUTHENTICATION_FAIL,
  NO_USER_ACCOUNT_ERROR,
  BAD_REQUEST_RESPONSE_ERROR
} from "../redux/constants/messageTypes";
import { jwtDecode } from "jwt-decode";

export default class UserAuth {
  //localStorage ssoUserToken
  /**
   * Stores the provided access token in the browser's localStorage under the key "ssoUserToken".
   *
   * @param {string} accessToken - The Single Sign-On (SSO) user access token to be stored.
   */
  setSSOUserToken(accessToken) {
    localStorage.setItem("ssoUserToken", accessToken);
  }

  getSSOUserToken() {
    return localStorage.getItem("ssoUserToken");
  }

  removeSSOUserToken() {
    localStorage.removeItem("ssoUserToken");
  }

  isIfsUserLoginRequired(response) {
    if (response && response.status === 401) {
      return true;
    }
    return false;
  }

  async getTeamsToken() {
    return authentication.getAuthToken({
      resources: [localStorage.getItem("applicationIDURI")],
    });
  }

  async getUserToken(refreshToken) {
    let tokenResponse;
    if (refreshToken) {
      const accessToken = this.getSSOUserToken();
      if (accessToken) {
        const decoded = jwtDecode(accessToken);
        const now = Date.now() / 1000;
        if (decoded.exp && decoded.exp > now) {
          tokenResponse = {
            accessToken: this.getSSOUserToken(),
            responseCode: 200,
            refreshToken: false,
          };

          return tokenResponse;
        }
      }
    }
    this.removeSSOUserToken();

    const teamsConfig = new AuthTeams();
    const tenantId = teamsConfig.getTenantId();
    try {
      const teamsToken = await this.getTeamsToken();
      if (!teamsToken) {
        tokenResponse = {
          accessToken: null,
          errorMessage: TEAMS_TOKEN_FAILURE,
          responseCode: 404,
          refreshToken: refreshToken,
        };
        return tokenResponse;
      }
      const baseURL = new CustomerConfig().getServiceApi();
      const client = axios.create({ baseURL, timeout: 60000 });
      client.defaults.headers.common.authorization = `Bearer ${teamsToken}`;
      client.defaults.headers.common["Ocp-Apim-Subscription-Key"] =
        new CustomerConfig().getSubscriptionKey();
      client.defaults.headers.common["Customer-Key"] =
        new AuthTeams().getCustomerKey() || process.env.REACT_APP_CLIENT_ID || "";
      client.defaults.headers.common["User-Key"] = new AuthTeams().getUserKey();
      await client
        .get(
          `api/User/get-usertoken?tenantId=${encodeURIComponent(tenantId ?? "")}`
        )
        .then(async (res) => {
          switch (res.status) {
            case 200:
              if (!res.data) {
                tokenResponse = {
                  accessToken: null,
                  errorMessage: AZURE_USER_AUTHENTICATION_FAILURE,
                  responseCode: 401,
                  refreshToken: refreshToken,
                };
              } else {
                this.setSSOUserToken(res.data);
                await getUserEmail(res.data);
                tokenResponse = {
                  accessToken: res.data,
                  responseCode: 200,
                  refreshToken: refreshToken,
                };
              }
              break;
            case 204:
              tokenResponse = {
                accessToken: null,
                errorMessage: NO_USER_ACCOUNT_ERROR,
                responseCode: 204,
                refreshToken: refreshToken,
              };
              break;

            default:
              let errorMessage = SERVER_RESPONSE_ISSUE;
              if (res.data !== undefined && res.data !== null) {
                errorMessage =
                  typeof res.data === "string"
                    ? res.data
                    : SERVER_RESPONSE_ISSUE;
              }
              tokenResponse = {
                accessToken: null,
                errorMessage: errorMessage,
                responseCode: res.status,
                refreshToken: refreshToken,
              };
              break;
          }
        })
        .catch((error) => {
          console.log(error?.toJSON?.() ?? error, "err");
          let errorMessage = "";
          if (error.response) {
            if (
              error.response.data !== undefined &&
              error.response.data !== null
            ) {
              errorMessage =
                typeof error.response.data === "string"
                  ? error.response.data
                  : "";
            }

            switch (error.response.status) {
              case 400:
                errorMessage = BAD_REQUEST_RESPONSE_ERROR;
                break;
              case 401:
                errorMessage = AZURE_USER_AUTHENTICATION_FAILURE;
                break;
              case 403:
                errorMessage = AUTHENTICATION_FAIL;
                break;
              case 404:
                errorMessage = NO_USER_ACCOUNT_ERROR;
                break;
              default:
                if (!errorMessage) {
                  errorMessage = SERVER_RESPONSE_ERROR;
                }
                break;
            }
          } else {
            errorMessage = `${SERVER_RESPONSE_ISSUE}`;
          }

          tokenResponse = {
            accessToken: null,
            errorMessage:
              errorMessage !== null && errorMessage !== ""
                ? errorMessage
                : `Error Code: ${
                    error.response?.status || 500
                  }: ${SERVER_RESPONSE_ERROR}`,
            responseCode: error.response?.status || 500,
            refreshToken: refreshToken,
          };
        });
    } catch (ex) {
      tokenResponse = {
        accessToken: null,
        errorMessage: ex.message,
        responseCode: 400,
        refreshToken: refreshToken,
      };
    }
    return tokenResponse;
  }
}

async function getUserEmail(token) {
  const graphResponse = await fetch("https://graph.microsoft.com/v1.0/me", {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!graphResponse.ok) {
    throw new Error("Failed to fetch user profile from Microsoft Graph.");
  }

  const userData = await graphResponse.json();
  localStorage.setItem(
    "userEmail",
    userData.mail || userData.userPrincipalName || ""
  );
}
